import { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import MoveIcon from "@mui/icons-material/Moving";
import RenameIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import { useWebServer } from "../../../../providers";
import {
  fetchOrganizations,
  fetchAllUsers,
  updateUserPermissions,
  deleteUser,
  moveDevice,
  renameDevice,
  setSuperUserUserFilterDebounced,
  setSuperUserDeviceFilterDebounced,
  displaySuccessNotification,
  displayErrorNotification,
  updateDeviceEnv,
  updateDeviceVectorType,
  updateDeviceHardwareId,
  fetchAlgVectorTypes,
} from "../../../../actions";
import DataTable from "../../..//DataTable/DataTable";
import { capitalize } from "../../../../utils/caseConvert";
import ManagePermissionsDialog from "./ManagePermissionsDialog";
import MoveDeviceDialog from "./MoveDeviceDialog";
import RenameDeviceDialog from "./RenameDeviceDialog";
import _ from "lodash";
import Typography from "@mui/material/Typography";
import ContextMenu from "./ContextMenu";
import Paper from "@mui/material/Paper";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { compareVersions, parseVersion } from "../../../../utils/versions";
import { environmentMapper } from "../../../../utils/environment";

const buttonStyles = {
  infoButton: {
    "&:hover, &:focus": {
      color: "status.info",
    },
  },
  successButton: {
    "&:hover, &:focus": {
      color: "status.success",
    },
  },
  editButton: {
    "&:hover, &:focus": {
      color: "status.info",
    },
  },
  deleteButton: {
    "&:hover, &:focus": {
      color: "status.danger",
    },
  },
};

const GROUP_COLUMNS = {
  users: [
    {
      field: "name",
      label: "Name",
      isSortable: true,
      align: "left",
      sx: {
        maxWidth: "100px",
      },
    },
    {
      field: "email",
      label: "Email",
      isSortable: true,
      align: "left",
      sx: {
        maxWidth: ["6rem", "6rem", "6rem", "13.75rem", "13.75rem", "13.75rem"],
      },
    },
    {
      field: "created_at",
      label: "Created At",
      isSortable: true,
      align: "left",
      sx: {
        maxWidth: ["6rem", "6rem", "6rem", "13.75rem", "13.75rem", "13.75rem"],
      },
      formatFn: (ts) =>
        ts
          ? new Date(ts).toLocaleString("en-us", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : "",
    },
  ],
  devices: [
    {
      field: "displayName",
      label: "Name",
      align: "left",
      sx: { maxWidth: "100px" },
    },
    {
      field: "uuid",
      label: "UUID",
      align: "left",
      sx: {
        minWidth: "3rem",
        maxWidth: ["8rem"],
      },
    },
    {
      field: "region",
      label: "Region",
      align: "center",
      sx: {
        maxWidth: ["6rem"],
      },
    },
    {
      field: "env",
      label: "Env",
      align: "center",
      sx: {
        maxWidth: ["3rem"],
      },
    },
    {
      field: "hardwareId",
      label: "HW_ID",
      align: "center",
      sx: {
        maxWidth: ["6rem"],
      },
    },
    {
      field: "algVersion",
      label: "Algorithm",
      align: "center",
      sx: {
        maxWidth: "3rem",
      },
      heightLightColor: (field) => {
        // a device with no parseable algVersion is not "behind", it is unknown -
        // compareVersions now sorts those below every real version, so the check
        // has to be explicit rather than riding on a string comparison
        if (!parseVersion(field)) return "default";
        return compareVersions(field, "v5.0.0") === -1 ? "red" : "default";
      },
    },
  ],
};

const Index = ({
  organizations,
  suserOrg,
  superUser,
  allUsers,
  devices,
  deviceFilter = "",
  userFilter = "",
  selected = "",
  fullDevices,
  fetchOrganizations,
  fetchAllUsers,
  updateUserPermissions,
  deleteUser,
  moveDevice,
  renameDevice,
  displaySuccessNotification,
  displayErrorNotification,
  setSuperUserUserFilterDebounced,
  setSuperUserDeviceFilterDebounced,
  updateDeviceEnv,
  updateDeviceVectorType,
  updateDeviceHardwareId,
  fetchAlgVectorTypes,
  possibleVectorTypes,
}) => {
  const { sendRequest, sendOrganizationRequest } = useWebServer();
  const [org, setOrg] = useState("");
  const [group, setGroup] = useState("");
  const [data, setData] = useState("");
  const [loading, setLoading] = useState(false);
  const [managingUser, setManagingUser] = useState(null);
  // Right-click target for the user table. Devices have their own menu
  // component; users need only the one item, so it lives here.
  const [userMenu, setUserMenu] = useState(null);
  const [movingDevice, setMovingDevice] = useState(null);
  const [renamingDevice, setRenamingDevice] = useState(null);
  const [userFilterVal, setUserFilterVal] = useState(`${userFilter}`);
  const [deviceFilterVal, setDeviceFilterVal] = useState(`${deviceFilter}`);
  const contextMenuRef = useRef();

  useEffect(() => {
    fetchAlgVectorTypes(sendRequest);
  }, [sendRequest, fetchAlgVectorTypes]);

  useEffect(() => {
    setSuperUserUserFilterDebounced(userFilterVal);
  }, [setSuperUserUserFilterDebounced, userFilterVal]);

  useEffect(() => {
    setUserFilterVal(userFilter);
  }, [userFilter, setUserFilterVal]);

  useEffect(() => {
    setSuperUserDeviceFilterDebounced(deviceFilterVal);
  }, [setSuperUserDeviceFilterDebounced, deviceFilterVal]);

  useEffect(() => {
    setDeviceFilterVal(deviceFilter);
  }, [deviceFilter, setDeviceFilterVal]);

  useEffect(() => {
    if (superUser) {
      fetchOrganizations(sendOrganizationRequest);
    }
  }, [fetchOrganizations, sendOrganizationRequest, superUser]);

  useEffect(() => {
    if (group === "users" && org !== "") {
      // Bucketed by IDX MEMBERSHIP, not by the user's primary organization.
      // Membership is what actually grants access, and someone can hold IDX
      // access to an organization that is not their primary one - listing them
      // by primary would hide them from the very organization they can see.
      //
      // A consequence worth knowing: a user with memberships in several
      // organizations appears under each of them. That is correct - they really
      // do belong to all of them - so the buckets are not a partition.
      //
      // "Unaffiliated" is therefore "no IDX access": no membership for any
      // organization IDX has. That covers a user never granted one and a user
      // whose organization has since been deleted, which are equally stranded
      // and need the same fix. It is bucketed here rather than by the server
      // because it can only be answered against the organization list, which
      // the user service cannot see.
      const known = new Set(Object.keys(organizations || {}));
      const inBucket = (user) => {
        // memberships arrive already filtered to IDX by the user service.
        const idxOrgs = (user?.memberships || []).map((m) => m.org);
        return org === "orgless"
          ? !idxOrgs.some((o) => known.has(o))
          : idxOrgs.includes(org);
      };

      setData(
        (allUsers || [])
          .filter(inBucket)
          .filter((user) => user?.email?.includes?.(userFilter))
          .map((user) => ({
            name: user.name,
            email: user.email,
            id: user.user_id,
            created_at: user.created_at
              ? new Date(user.created_at).getTime()
              : null,
            organization: user?.metadata?.organization || "orgless",
            // Carried onto the row so the permissions dialog opens already
            // showing what the user has, without a second request.
            memberships: user.memberships || [],
          })),
      );
    }
  }, [allUsers, organizations, org, group, userFilter]);

  useEffect(() => {
    if (group === "devices" && org !== "") {
      const processDevices = (devices) =>
        devices.reduce(
          (arr, device) =>
            device?.uuid?.includes?.(deviceFilter)
              ? [
                  ...arr,
                  {
                    displayName: device.display_name,
                    uuid: device.uuid,
                    id: device.uuid,
                    hardwareId: device.hardware_id,
                    organization: org,
                    algVersion:
                      fullDevices?.[device.uuid]?.["algVersion"] || "",
                    region: fullDevices?.[device.uuid]?.["vectorType"] || "",
                    env: environmentMapper(fullDevices?.[device.uuid]?.["env"]),
                  },
                ]
              : arr,
          [],
        );
      setData(processDevices(devices?.[org] || {}));
    }
  }, [devices, org, group, setData, deviceFilter, fullDevices]);

  useEffect(() => {
    if (_.isString(selected)) {
      if (selected === "") {
        setOrg("");
        setGroup("");
      } else {
        const [currOrg, currGroup] = selected.split(";");
        setOrg(currOrg);
        setGroup(currGroup);
      }
    }
  }, [selected]);

  // One fetch for every bucket. Previously this ran per organization on every
  // selection; the panel now needs a count for all of them at once anyway.
  useEffect(() => {
    if (group === "users") {
      setLoading(true);
      fetchAllUsers(sendRequest).then(() => setLoading(false));
    }
  }, [sendRequest, fetchAllUsers, group]);

  const isSelected = () =>
    [group, org].reduce(
      (curr, x) => curr & _.isString(x) && x.length > 0,
      true,
    );

  const handleManagePermissions = async (user, organizations) =>
    updateUserPermissions(
      sendRequest,
      user.id,
      organizations,
      user.name || user.email,
    );

  const handleDeleteUser = async (user) => {
    if (user?.id) {
      return deleteUser(sendRequest, org, user.id, user.name);
    } else {
      displayErrorNotification("User was not found. Failed to delete user...");
    }
  };

  const handleMoveDevice = async (device, oldOrg, newOrg) => {
    if (organizations?.[newOrg]) {
      return moveDevice(sendRequest, device, oldOrg, newOrg, suserOrg);
    } else {
      displayErrorNotification(
        "New organization was invalid. Failed to move device...",
      );
    }
  };

  const handleRenameDevice = async (device, name) => {
    return renameDevice(sendRequest, device, name, org);
  };

  const getActions = () => {
    if (group === "users") {
      return [
        {
          name: "Delete",
          icon: DeleteIcon,
          position: "primary",
          dialog: (row) =>
            `Are you sure you want to delete ${row.name} (${row.email})?\nThis action cannot be undone`,
          sx: buttonStyles.deleteButton,
          handler: handleDeleteUser,
        },
      ];
    } else if (group === "devices") {
      return [
        {
          name: "Move",
          icon: MoveIcon,
          position: "primary",
          dialog: null,
          sx: buttonStyles.infoButton,
          handler: (device) => setMovingDevice(device),
        },
        {
          name: "Rename",
          icon: RenameIcon,
          position: "primary",
          dialog: null,
          sx: buttonStyles.infoButton,
          handler: (device) => setRenamingDevice(device),
        },
      ];
    }
    return [];
  };

  // context menu
  const onUpdateDeviceEnv = (deviceUuid, newEnv) => {
    updateDeviceEnv(deviceUuid, newEnv);
  };

  const onUpdateDeviceVectorType = (deviceUuid, newVectorType) => {
    updateDeviceVectorType(deviceUuid, newVectorType);
  };

  const onUpdateDeviceHardwareId = (deviceUuid, newHardwareId) => {
    updateDeviceHardwareId(sendRequest, deviceUuid, newHardwareId, org);
  };

  return (
    <Paper
      className={
        "flex bg-white rounded-xl border-4 border-transparent transition-opacity duration-1000"
      }
      sx={{
        py: "1rem",
        pt: "3rem",
        px: "1rem",
        width: ["100vw", "100%", "100%", "100%", "100%", "100%"],
        height: "100vh",
      }}
    >
      {superUser ? (
        <div id={"superuserpanel-datatable"} style={{ width: "100%" }}>
          {isSelected() ? (
            <>
              <ContextMenu
                ref={contextMenuRef}
                devices={fullDevices}
                organizations={organizations}
                possibleVectorTypes={possibleVectorTypes}
                updateDeviceEnv={onUpdateDeviceEnv}
                updateDeviceVectorType={onUpdateDeviceVectorType}
                updateDeviceHardwareId={onUpdateDeviceHardwareId}
              />
              <ManagePermissionsDialog
                user={managingUser}
                organizations={organizations}
                setUser={setManagingUser}
                onConfirm={handleManagePermissions}
              />
              <Menu
                open={!!userMenu}
                onClose={() => setUserMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                  userMenu
                    ? { top: userMenu.mouseY, left: userMenu.mouseX }
                    : undefined
                }
              >
                <MenuItem
                  onClick={() => {
                    setManagingUser(userMenu?.user || null);
                    setUserMenu(null);
                  }}
                >
                  Manage Permissions
                </MenuItem>
              </Menu>
              <MoveDeviceDialog
                actionName={"Move"}
                device={movingDevice}
                org={org}
                organizations={organizations}
                setDevice={setMovingDevice}
                onConfirm={handleMoveDevice}
              />
              <RenameDeviceDialog
                actionName={"Rename"}
                device={renamingDevice}
                setDevice={setRenamingDevice}
                onConfirm={handleRenameDevice}
              />
              <Grid
                container
                spacing={2}
                sx={{
                  "& .MuiTextField-root": { m: 1, minWidth: "25ch" },
                }}
                id={"superuser-filters-form"}
              >
                <Grid size={2}></Grid>
                <Grid size={3}>
                  <Typography
                    sx={{
                      my: [
                        "0.25rem",
                        "0.25rem",
                        "0.25rem",
                        "0.25rem",
                        "0.5rem",
                        "0.5rem",
                      ],
                      fontSize: [
                        "1.25rem",
                        "1.25rem",
                        "1.25rem",
                        "1.25rem",
                        "1.75rem",
                        "1.75rem",
                      ],
                    }}
                  >
                    {`${capitalize(group)}`}
                  </Typography>
                </Grid>
                <Grid size={7}>
                  {group === "users" ? (
                    <TextField
                      id="outlined-dt-search-field"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      label={"Users"}
                      value={userFilterVal}
                      onChange={(event) =>
                        setUserFilterVal(event?.target?.value || "")
                      }
                    />
                  ) : group === "devices" ? (
                    <TextField
                      id="outlined-dt-search-field"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      label={"Devices"}
                      value={deviceFilterVal}
                      onChange={(event) =>
                        setDeviceFilterVal(event?.target?.value || "")
                      }
                    />
                  ) : null}
                </Grid>
                <Grid size={12}>
                  <DataTable
                    dataList={data}
                    total={data?.length || 0}
                    tableName={`${capitalize(group)}`}
                    columns={GROUP_COLUMNS?.[group]}
                    isFetchingData={loading}
                    options={{
                      allowCreate: false,
                      allowClick: false,
                      allowContext: true,
                    }}
                    actions={getActions()}
                    // handleAddData={() => {}}
                    // onClickRow={(row) => {}}
                    onContextMenu={(e, row) => {
                      if (group === "users") {
                        e.preventDefault();
                        setUserMenu({
                          mouseX: e.clientX + 2,
                          mouseY: e.clientY - 6,
                          user: row,
                        });
                        return;
                      }
                      if (row?.organization === suserOrg) {
                        contextMenuRef.current.handleContextMenu(
                          e,
                          row?.uuid,
                          row?.organization,
                          row,
                        );
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography
              sx={{
                my: [
                  "0.25rem",
                  "0.25rem",
                  "0.25rem",
                  "0.25rem",
                  "0.5rem",
                  "0.5rem",
                ],
                fontSize: [
                  "1.25rem",
                  "1.25rem",
                  "1.25rem",
                  "1.25rem",
                  "1.75rem",
                  "1.75rem",
                ],
              }}
            >
              Please select datatype
            </Typography>
          )}
        </div>
      ) : (
        <Typography
          sx={{
            my: [
              "0.25rem",
              "0.25rem",
              "0.25rem",
              "0.25rem",
              "0.5rem",
              "0.5rem",
            ],
            fontSize: [
              "1.25rem",
              "1.25rem",
              "1.25rem",
              "1.25rem",
              "1.75rem",
              "1.75rem",
            ],
            color: "red",
          }}
        >
          Access Denied
        </Typography>
      )}
    </Paper>
  );
};

export const mapStateToProps = (state) => {
  const organizations = state?.user?.organizations || {};
  const suserOrg = state?.user?.organization || "";
  const superUser = state?.user?.superUser || false;
  const allUsers = state?.user?.allUsers || [];
  const devices = state?.user?.orgDevices || {};
  const fullDevices = state?.devices;

  const { deviceFilter, userFilter, selected } = state?.superuser;
  return {
    superUser,
    organizations,
    suserOrg,
    allUsers,
    devices,
    possibleVectorTypes: state?.algVersion?.vectorTypes,
    deviceFilter,
    userFilter,
    selected,
    fullDevices,
  };
};

export default connect(mapStateToProps, {
  fetchOrganizations,
  fetchAllUsers,
  updateUserPermissions,
  deleteUser,
  moveDevice,
  renameDevice,
  displaySuccessNotification,
  displayErrorNotification,
  setSuperUserUserFilterDebounced,
  setSuperUserDeviceFilterDebounced,
  updateDeviceEnv,
  updateDeviceVectorType,
  updateDeviceHardwareId,
  fetchAlgVectorTypes,
})(Index);
